import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { PersonApi } from './usePeople';

/**
 * Attendee membership of one mobilisation: who is in it, who could be
 * added, attach, detach. Backs the attendees panel on the
 * mobilisations admin page.
 */

export interface AttendeeCandidate {
  id: string;
  full_name: string;
  email: string;
  position: string | null;
}

const attendeesKey = (id: string) => ['mobilisation-attendees', id] as const;
const candidatesKey = (id: string, q: string) =>
  ['mobilisation-attendee-candidates', id, q] as const;

export function useMobilisationAttendees(mobilisationId: string | null) {
  return useQuery({
    queryKey: attendeesKey(mobilisationId ?? 'none'),
    enabled: !!mobilisationId,
    queryFn: async () =>
      (
        await api<{ attendees: PersonApi[] }>(
          `/api/mobilisations/${mobilisationId}/attendees`,
        )
      ).attendees,
  });
}

export function useAttendeeCandidates(mobilisationId: string | null, q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: candidatesKey(mobilisationId ?? 'none', query),
    // Enabled with an empty query on purpose: a fresh mobilisation's
    // panel should list addable people before anyone types a search.
    enabled: !!mobilisationId,
    queryFn: async () =>
      (
        await api<{ people: AttendeeCandidate[] }>(
          `/api/mobilisations/${mobilisationId}/attendees/candidates?q=${encodeURIComponent(query)}`,
        )
      ).people,
  });
}

function useInvalidateMembership(mobilisationId?: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({
      queryKey: mobilisationId
        ? attendeesKey(mobilisationId)
        : ['mobilisation-attendees'],
    });
    void qc.invalidateQueries({
      queryKey: mobilisationId
        ? ['mobilisation-attendee-candidates', mobilisationId]
        : ['mobilisation-attendee-candidates'],
    });
    // Scoped lists elsewhere (reception, coordinator, people admin)
    // change when membership changes.
    void qc.invalidateQueries({ queryKey: ['people'] });
    void qc.invalidateQueries({ queryKey: ['people-memberships'] });
  };
}

export function useAttachAttendee(mobilisationId: string) {
  const invalidate = useInvalidateMembership(mobilisationId);
  return useMutation({
    mutationFn: (person_id: string) =>
      api<void>(`/api/mobilisations/${mobilisationId}/attendees`, {
        method: 'POST',
        body: JSON.stringify({ person_id }),
      }),
    onSuccess: invalidate,
  });
}

export function useDetachAttendee(mobilisationId: string) {
  const invalidate = useInvalidateMembership(mobilisationId);
  return useMutation({
    mutationFn: (person_id: string) =>
      api<void>(`/api/mobilisations/${mobilisationId}/attendees/${person_id}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}

/**
 * Mobilisation-varying variants for the person record page, where one
 * person is added to / removed from any of their mobilisations.
 */
export function useAttachPersonMobilisation() {
  const invalidate = useInvalidateMembership();
  return useMutation({
    mutationFn: (input: { mobilisation_id: string; person_id: string }) =>
      api<void>(`/api/mobilisations/${input.mobilisation_id}/attendees`, {
        method: 'POST',
        body: JSON.stringify({ person_id: input.person_id }),
      }),
    onSuccess: invalidate,
  });
}

export function useDetachPersonMobilisation() {
  const invalidate = useInvalidateMembership();
  return useMutation({
    mutationFn: (input: { mobilisation_id: string; person_id: string }) =>
      api<void>(
        `/api/mobilisations/${input.mobilisation_id}/attendees/${input.person_id}`,
        { method: 'DELETE' },
      ),
    onSuccess: invalidate,
  });
}
